export const noop = () => {};

export const CI_TASK_HEADER_CHAR_LIMIT = 10;

export function getTaskHeaderLabel(taskTitle) {
  const full = String(taskTitle || '')
    .replace(/&quot;/gi, '')
    .replace(/["'`""''„‟«»]/g, '')
    .replace(/\s+/g, ' ')
    .trim() || 'Untitled Task';
  const display =
    full.length > CI_TASK_HEADER_CHAR_LIMIT
      ? `${full.slice(0, CI_TASK_HEADER_CHAR_LIMIT)}…`
      : full;
  return { full, display };
}

export function toMenuItems(values) {
  return values.map((value, index) => ({ id: `${value}-${index}`, title: value }));
}

export function getScoreCellClass(score) {
  const parts = String(score).split('/');
  if (parts.length !== 2) return '';

  const earned = Number(parts[0]);
  const total = Number(parts[1]);

  if (isNaN(earned) || isNaN(total)) return '';
  if (total === 0) return 'ci-cell-low';

  const percentage = (earned / total) * 100;

  if (percentage >= 67) return 'ci-cell-high';
  if (percentage >= 34) return 'ci-cell-middle';
  return 'ci-cell-low';
}

export function getAvgColor(avg) {
  if (avg >= 60) return '#32AF25';
  if (avg >= 40) return '#BF8609';
  return '#F14949';
}

export function getTierClass(tier) {
  const normalizedTier = String(tier ?? '').toUpperCase().trim();

  switch (normalizedTier) {
    case 'TOP':
      return 'ci-rubric-high';
    case 'MIDDLE':
      return 'ci-rubric-middle';
    case 'BOTTOM':
      return 'ci-rubric-low';
    default:
      return '';
  }
}

export function getRankClass(rank) {
  const baseClass = 'ci-student-rank';
  if (rank === 1) return `${baseClass} ${baseClass}--star ${baseClass}--star-1`;
  if (rank === 2) return `${baseClass} ${baseClass}--star ${baseClass}--star-2`;
  if (rank === 3) return `${baseClass} ${baseClass}--star ${baseClass}--star-3`;
  return baseClass;
}

// Shared by SuccessCriteriaPanel (small tile) and SuccessCriteriaModal (expanded).
// Both used to carry their own copy of these literals; keeping one definition is
// what stops the two views disagreeing about what "Highest to Lowest" means.
export const SUCCESS_CRITERIA_SORT_OPTIONS = [
  'Highest to Lowest Success',
  'Lowest to Highest Success',
];

export const SUCCESS_CRITERIA_DEFAULT_SORT = SUCCESS_CRITERIA_SORT_OPTIONS[0];

export function successCriteriaSortOrder(sortBy) {
  return sortBy === SUCCESS_CRITERIA_SORT_OPTIONS[0] ? 'desc' : 'asc';
}

export function sortSuccessCriteriaObjectives(objectives, sortOrder = 'asc') {
  const list = Array.isArray(objectives) ? objectives : [];
  if (list.length < 2) {
    return [...list];
  }
  const direction = sortOrder === 'desc' ? -1 : 1;
  return [...list].sort((a, b) => {
    const sa = a.satisfiedPercentage;
    const sb = b.satisfiedPercentage;
    if (sa !== sb) return direction * (sa - sb);
    const ra = a.notSatisfiedPercentage;
    const rb = b.notSatisfiedPercentage;
    if (ra !== rb) return direction * (rb - ra);
    return a.title.localeCompare(b.title, undefined, {
      sensitivity: 'base',
    });
  });
}

export function normalizeSuccessCriteriaPreviewData(data, fallback) {
  const candidate = Array.isArray(data)
    ? data.find((item) => String(item?.id) === String(fallback?.id)) || data[0]
    : data?.successCriteria || data?.criteria || data?.data || data;

  if (candidate && Array.isArray(candidate.criterias)) {
    return candidate;
  }

  const source = candidate && Array.isArray(candidate.objectives) ? candidate : fallback;
  if (!source || !Array.isArray(source.objectives)) {
    return fallback || null;
  }

  return {
    id: source.id || fallback?.id,
    title: source.title || fallback?.title || 'Untitled Success Criteria',
    criterias: [
      {
        objectives: source.objectives.map((objective) => ({
          description: objective?.description || objective?.title || '',
        })),
      },
    ],
  };
}
